/**
 * Admin Access Utilities
 * Centralized logic for determining admin access levels
 */

/**
 * Get admin emails from environment or use defaults
 */
const getAdminEmails = () => {
  const envEmails = process.env.REACT_APP_ADMIN_EMAILS;
  if (envEmails) {
    return envEmails.split(',').map(email => email.trim().toLowerCase());
  }
  
  // Default admin emails if not configured
  return [
    'admin@projectz.com',
    'malleshmadapathi@outlook.com',
  ];
};

/**
 * Check if user has admin privileges
 * @param {Object} user - User object from authentication context
 * @returns {boolean} - True if user is admin
 */
export const isAdmin = (user) => {
  if (!user) return false;
  
  // Check if user has admin role
  if (user.role === 'ADMIN' || user.isAdmin === true) {
    return true;
  }
  
  // Check if user email is in admin list
  const adminEmails = getAdminEmails();
  if (user.email && adminEmails.includes(user.email.toLowerCase())) {
    return true;
  }
  
  return false;
};

/**
 * Check if user has logs access based on current feature flags
 * @param {Object} user - User object from authentication context
 * @returns {boolean} - True if user can access logs
 */
export const hasLogsAccess = (user) => {
  // Check environment variable for restriction setting
  const restrictionEnabled = process.env.REACT_APP_LOGS_ACCESS_RESTRICTION === 'true';
  
  if (!restrictionEnabled) {
    return true; // Feature flag OFF - everyone has access
  }
  
  // If restriction is enabled, only admins have access
  return isAdmin(user);
};

/**
 * Check if user should see warning banner
 * @param {Object} user - User object from authentication context
 * @returns {boolean} - True if warning should be shown
 */
export const shouldShowLogsWarning = (user) => {
  // Check environment variable for warning setting
  const warningEnabled = process.env.REACT_APP_LOGS_ACCESS_WARNING === 'true';
  return warningEnabled && !isAdmin(user);
};

/**
 * Get admin access info for user
 * @param {Object} user - User object from authentication context
 * @returns {Object} - Admin access information
 */
export const getAdminAccessInfo = (user) => {
  return {
    isAdmin: isAdmin(user),
    hasLogsAccess: hasLogsAccess(user),
    shouldShowWarning: shouldShowLogsWarning(user),
    userEmail: user?.email,
    userRole: user?.role || 'USER'
  };
};